import React, { useEffect, useMemo, useRef } from 'react'
import { Message } from '../../../../../utils/conversationStorage'
import type { DesktopSuggestion } from '../../dashboardActionsConfig.tsx'
import { useTypewriter } from '../../../../../hooks/useTypewriter.ts'
import { useFloatingAgentMessage } from '../../../../FloatingAgent/FloatingAgentMessageContext.tsx'
import PostPreviewCard from './PostPreviewCard.tsx'
import { parseProjectAutofill } from '../../../../../utils/conversationalMode/projectAutofillDraftUtils.ts'
import { isGuideMessage } from '../../../../../utils/conversationalMode/guideMessageUtils.ts'
import { parsePortfolioProgress } from '../../../../../utils/conversationalMode/portfolioLlmProgress.ts'
import styles from './MessageBubble.module.scss'
import { formatMessageText } from './formatMessageText.tsx'
import type { ChatbotSuggestedAction } from '../../../../../services/llmService.ts'
import CreatedPortfolioCard from './CreatedPortfolioCard.tsx'
import CreatedPostCard from './CreatedPostCard.tsx'
import DocumentInsightPanel from '../DocumentInsightPanel/DocumentInsightPanel.tsx'
import ProjectAutofillCard from './ProjectAutofillCard.tsx'
import GuideMessageBubble from './GuideMessageBubble.tsx'
import PortfolioProgressSection from './PortfolioProgressSection.tsx'

interface MessageBubbleProps {
  message: Message
  suggestion?: DesktopSuggestion | null
  onSuggestionAction?: () => void
  onRequestGuide?: () => void
  isLatest?: boolean
  onTypingStateChange?: (messageId: string, isTyping: boolean) => void
  userData?: any
  pendingImagePreview?: string | null
  suggestedActions?: ChatbotSuggestedAction[]
  onSuggestedChip?: (label: string, actionId: string) => void
  onRequestOpenAttachmentPicker?: () => void
}

const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  suggestion,
  onSuggestionAction,
  onRequestGuide,
  isLatest = false,
  onTypingStateChange,
  userData,
  pendingImagePreview = null,
  suggestedActions,
  onSuggestedChip,
  onRequestOpenAttachmentPicker
}) => {
  const isUser = message.role === 'user'
  const isGuide = !isUser && isGuideMessage(message.content)
  const shouldAnimate = !isUser && isLatest
  const contentForDisplay =
    !isUser && message.createdPortfolio?.type === 'servicio'
      ? message.content.replace(/\bproducto\b/gi, 'servicio')
      : message.content
  const { displayedText, isTyping } = useTypewriter(contentForDisplay, shouldAnimate)
  const parsedProjectAutofillPayload = useMemo(
    () => parseProjectAutofill(message.llmRawResponse),
    [message.llmRawResponse]
  )
  const projectAutofillPayload = !isUser ? parsedProjectAutofillPayload : null
  const inFloatingAgent = useFloatingAgentMessage()

  const previousTypingState = useRef<boolean | undefined>(undefined)
  const callbackRef = useRef(onTypingStateChange)

  useEffect(() => {
    callbackRef.current = onTypingStateChange
  }, [onTypingStateChange])

  useEffect(() => {
    if (previousTypingState.current !== isTyping) {
      previousTypingState.current = isTyping
      if (callbackRef.current) {
        callbackRef.current(message.id, isTyping)
      }
    }
  }, [isTyping, message.id])

  if (isGuide) {
    return (
      <GuideMessageBubble
        messageId={message.id}
        displayedText={displayedText}
        isTyping={isTyping}
        inFloatingAgent={inFloatingAgent}
        suggestion={suggestion}
        messageActionId={message.actionId}
        onSuggestionAction={onSuggestionAction}
        onRequestGuide={onRequestGuide}
        suggestedActions={suggestedActions}
        onSuggestedChip={onSuggestedChip}
      />
    )
  }

  const showActionButton = !isUser && suggestion && message.actionId === suggestion.id && onSuggestionAction && !isTyping

  const textToShow = isUser ? message.content : displayedText
  const portfolioProgress = !isUser ? parsePortfolioProgress(message.llmRawResponse) : null
  const portfolioItems = portfolioProgress?.items ?? null
  const summaryImageUrl = portfolioProgress?.imagePreviewUrl
  const createdPortfolio = message.createdPortfolio

  let postPreviewContent: string | null = null
  let postPreviewImageUrl: string | null = null
  let textWhenPostPreview = textToShow
  if (!isUser && message.llmRawResponse && message.actionId === 'guardar-post') {
    try {
      const parsed = JSON.parse(message.llmRawResponse)
      const content = parsed?.collectedData?.content
      if (content) {
        postPreviewContent = content
        postPreviewImageUrl = parsed?.imagePreviewUrl ?? pendingImagePreview ?? null
        const questionPart = message.content
          ?.replace(content, '')
          ?.replace(/Acá va la propuesta:\s*\n*/i, '')
          ?.replace(/\n{2,}/g, '\n')
          ?.trim()
        if (questionPart) textWhenPostPreview = questionPart
      }
    } catch {}
  }

  return (
    <div
      className={`${styles.messageBubble} ${
        isUser ? styles.userMessage : styles.assistantMessage
      } ${projectAutofillPayload ? styles.projectAutofillMessage : ''} ${isTyping ? styles.typing : ''} ${inFloatingAgent ? styles.inFloatingAgent : ''}`}
    >
      <div className={styles.messageContent}>
        {postPreviewContent && (
          <PostPreviewCard
            content={postPreviewContent}
            userData={userData}
            imagePreviewUrl={postPreviewImageUrl}
          />
        )}
        {portfolioItems && portfolioItems.length > 0 && (
          <PortfolioProgressSection
            portfolioItems={portfolioItems}
            summaryImageUrl={summaryImageUrl}
          />
        )}
        {projectAutofillPayload && (
          <ProjectAutofillCard
            payload={projectAutofillPayload}
            onRequestChangeSourceDocument={onRequestOpenAttachmentPicker}
          />
        )}
        {!isUser && message.documentInsight ? (
          <DocumentInsightPanel
            insight={message.documentInsight}
            handlers={
              onRequestOpenAttachmentPicker
                ? { onUploadTechnicalDoc: onRequestOpenAttachmentPicker }
                : undefined
            }
          />
        ) : null}
        <div className={styles.messageText}>
          {isUser
            ? formatMessageText(message.content, message.id)
            : formatMessageText(postPreviewContent ? textWhenPostPreview : textToShow, message.id)}
          {isTyping && <span className={styles.cursor}>|</span>}
        </div>

        {createdPortfolio && !isUser && <CreatedPortfolioCard portfolio={createdPortfolio} />}

        {message.createdPost && !isUser && <CreatedPostCard post={message.createdPost} />}

        {showActionButton && (
          <button
            type="button"
            className={styles.actionLink}
            onClick={onSuggestionAction}
            aria-label={suggestion.label}
          >
            {suggestion.label} →
          </button>
        )}
        {suggestedActions &&
          suggestedActions.length > 0 &&
          onSuggestedChip &&
          !isTyping && (
            <div className={styles.suggestedActionsRow} role="group" aria-label="Acciones sugeridas">
              {suggestedActions.map((a, i) => (
                <button
                  key={`${a.actionId}-${i}`}
                  type="button"
                  className={styles.suggestedActionChip}
                  onClick={() => onSuggestedChip(a.label, a.actionId)}
                >
                  {a.label}
                </button>
              ))}
            </div>
          )}
      </div>
    </div>
  )
}

export default MessageBubble
